import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Container } from '@mui/material';
import api from '../services/api';
import { CategoryWithSubcategories, OrderProduct } from '../types/order';
import PlanCarousel from '../components/order/PlanCarousel';

/**
 * Embeddable plan carousel for use in iframes on the main website.
 * URL: /order/embed?category=internet&subcategory=supa
 * Optional: &product=supa-2000-2000 to pre-select a plan (visual only; "Order" still links with that slug).
 * When the user clicks "Order this plan", they are taken to the full order page with the deep link
 * e.g. https://order.sonicnet.net.au/order?category=internet&subcategory=supa&product=supa-2000-2000
 */
function OrderEmbedPage() {
  const [searchParams] = useSearchParams();
  const categorySlug = searchParams.get('category') || '';
  const subcategorySlug = searchParams.get('subcategory') || '';
  const productSlug = searchParams.get('product') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<OrderProduct[]>([]);
  const [productFieldsConfig, setProductFieldsConfig] = useState<{ field_key: string; display_order: number; label_override?: string; is_visible: boolean }[]>([]);
  const [subcategoryName, setSubcategoryName] = useState<string>('Plans');
  const [selectedPlan, setSelectedPlan] = useState<OrderProduct | null>(null);

  const orderPageBaseUrl =
    (process.env.REACT_APP_ORDER_PUBLIC_URL || window.location.origin).replace(/\/$/, '');

  // Embed: transparent background so it blends into the parent page (theme default is grey #f0f2f5)
  useEffect(() => {
    const prevBody = document.body.style.backgroundColor;
    const prevHtml = document.documentElement.style.backgroundColor;
    document.body.style.backgroundColor = 'transparent';
    document.documentElement.style.backgroundColor = 'transparent';
    return () => {
      document.body.style.backgroundColor = prevBody;
      document.documentElement.style.backgroundColor = prevHtml;
    };
  }, []);

  useEffect(() => {
    if (!categorySlug || !subcategorySlug) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      api.getOrderCategories(),
      api.getOrderProducts({ category: categorySlug, subcategory: subcategorySlug, type: 'internet_plan' }),
      api.getOrderProductFields({ category: categorySlug, subcategory: subcategorySlug }).catch(() => []),
    ])
      .then(([cats, prods, fields]) => {
        if (cancelled) return;
        const categoriesData = cats as CategoryWithSubcategories[];
        const category = categoriesData.find((c) => c.slug === categorySlug);
        const subcat = category?.subcategories?.find((s) => s.slug === subcategorySlug);
        if (subcat) setSubcategoryName(subcat.name);
        const sorted = [...(prods as OrderProduct[])].sort(
          (a, b) => Number(a.display_order ?? 0) - Number(b.display_order ?? 0) || (a.name || '').localeCompare(b.name || '')
        );
        setProducts(sorted);
        setProductFieldsConfig(
          (fields as { field_key: string; display_order: number; label_override?: string; is_visible: boolean }[])
            .filter((f) => f.is_visible)
            .sort((a, b) => a.display_order - b.display_order)
        );
        const preSelect = productSlug ? sorted.find((p) => p.slug === productSlug) ?? null : null;
        setSelectedPlan(preSelect);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to load plans');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [categorySlug, subcategorySlug, productSlug]);

  if (!categorySlug || !subcategorySlug) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Add <code>?category=internet&amp;subcategory=supa</code> to the URL to show plans. For example:{' '}
          <code>/order/embed?category=internet&amp;subcategory=supa</code>
        </Typography>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 280, py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  if (products.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No plans available for this category.</Typography>
      </Container>
    );
  }

  const getOrderLink = (plan: OrderProduct) =>
    `${orderPageBaseUrl}/order?category=${encodeURIComponent(categorySlug)}&subcategory=${encodeURIComponent(subcategorySlug)}&product=${encodeURIComponent(plan.slug)}`;

  return (
    <Box sx={{ py: 2, px: 1 }}>
      <PlanCarousel
        plans={products}
        selectedPlan={selectedPlan}
        onSelectPlan={setSelectedPlan}
        subcategoryName={subcategoryName}
        productFieldsConfig={productFieldsConfig.length > 0 ? productFieldsConfig : undefined}
        getOrderLink={getOrderLink}
        hideHeading
      />
    </Box>
  );
}

export default OrderEmbedPage;
